// uiarc: drawer (https://uiarc.dev/r/drawer.json) — Base UI + Tailwind'e çevrildi. Dosya adı ve API (`Sheet*`)
// çağıran yerler için korundu; görünüm ve hareket uiarc drawer'dır. Panel kenarından yayla gelir (`drawer-motion`:
// index.css), perde bulanıklaşır; kapanış daha kısa, gölge son aşamada solar. Kapanırken tıklama içinden geçer.
// Uygulanmayanlar: başlıktan sürükleyerek kapatma, `container` (kapsayıcı içi), alttan açılışta tutamaç hareketi.
import * as React from 'react'
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { cn } from '@/lib/utils'

import { OverlayClose } from '@/components/ui/overlay-close'
import { SwapContent } from '@/components/ui/swap-text'

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        'backdrop-motion fixed inset-0 z-50 bg-[oklch(10%_0_0/0.38)] backdrop-blur-[4px]',
        className,
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'drawer-motion border-border bg-surface-raised text-foreground shadow-floating fixed z-51 flex flex-col overflow-hidden outline-none',
          'max-h-dvh max-w-screen',
          'data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:w-[min(30rem,calc(100vw-1rem))] data-[side=right]:rounded-l-[22px] data-[side=right]:border-y data-[side=right]:border-l',
          'data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:w-[min(30rem,calc(100vw-1rem))] data-[side=left]:rounded-r-[22px] data-[side=left]:border-y data-[side=left]:border-r',
          'data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:max-h-[min(32rem,100dvh)] data-[side=top]:w-full data-[side=top]:max-w-none data-[side=top]:rounded-b-[22px] data-[side=top]:border-b',
          'data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:max-h-[min(32rem,100dvh)] data-[side=bottom]:w-full data-[side=bottom]:max-w-none data-[side=bottom]:rounded-t-[22px] data-[side=bottom]:border-t',
          // Alttan açılışta tutamaç çizgisi.
          'data-[side=bottom]:before:bg-border-strong data-[side=bottom]:before:rounded-pill data-[side=bottom]:before:absolute data-[side=bottom]:before:top-[9px] data-[side=bottom]:before:left-1/2 data-[side=bottom]:before:h-1 data-[side=bottom]:before:w-9 data-[side=bottom]:before:-translate-x-1/2 data-[side=bottom]:before:opacity-75 data-[side=bottom]:before:content-[""]',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <OverlayClose className="absolute top-[calc(1.5rem+(1.125rem*1.4-2rem)/2)] right-5 sm:right-6" />
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'border-border flex flex-none flex-col gap-2 border-b p-5 pr-[4.75rem] sm:p-6 sm:pr-[4.75rem]',
        className,
      )}
      {...props}
    />
  )
}

/** uiarc gövde alanı: kendi içinde kayar, kaydırma zincirlenmez. */
function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-body"
      className={cn(
        'leading-body min-h-0 flex-1 [scrollbar-width:thin] overflow-auto overscroll-contain p-5 text-sm sm:p-6',
        className,
      )}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('border-border mt-auto flex flex-col gap-2 border-t p-5 sm:p-6', className)}
      {...props}
    />
  )
}

function SheetTitle({ className, children, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        'text-foreground leading-body relative m-0 text-lg font-medium tracking-[-0.01em]',
        className,
      )}
      {...props}
    >
      <SwapContent>{children as React.ReactNode}</SwapContent>
    </SheetPrimitive.Title>
  )
}

function SheetDescription({ className, children, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-text-secondary leading-body relative m-0 max-w-prose text-sm', className)}
      {...props}
    >
      <SwapContent>{children as React.ReactNode}</SwapContent>
    </SheetPrimitive.Description>
  )
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
